import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  DataTable,
  Button,
  Badge,
  EmptyState,
  Spinner,
  BlockStack,
  InlineStack,
  Text,
  Checkbox,
  Banner,
  TextField
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function OrdersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [noShopContext, setNoShopContext] = useState(false);
  const [demoShopInput, setDemoShopInput] = useState('');
  const [storeNotFound, setStoreNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [orderFilter, setOrderFilter] = useState('unfulfilled');

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      setStoreNotFound(false);
      setFetchError(null);
      fetchOrders(shopParam);
      fetchBillingStatus(shopParam);
    } else {
      // No shop context available - this is expected in preview/dev environments
      setLoading(false);
      setNoShopContext(true);
    }
  }, [searchParams]);

  const fetchOrders = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/orders`, {
        params: { shop: shopDomain }
      });
      const data = response.data;
      setOrders(data.orders || []);
      setOrderFilter(data.filter || 'unfulfilled');
      setStoreNotFound(false);
      setFetchError(null);
      
      // Log for debugging
      console.log(`[ShipMate] Fetched ${data.count || 0} orders (filter: ${data.filter})`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to fetch orders';
      
      console.error(`[ShipMate] Error fetching orders:`, detail);
      
      if (status === 404 || status === 401) {
        // Store not found or access token invalid - OAuth installation required
        setStoreNotFound(true);
        setFetchError(detail);
      } else {
        // Other error - show the error but keep the page functional
        setFetchError(detail);
        showToast(detail, true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingStatus = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/billing/status`, {
        params: { shop: shopDomain }
      });
      setBillingStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch billing status');
    }
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${encodeURIComponent(orderId)}?shop=${shop}`);
  };

  const handleGoToSettings = () => {
    navigate(`/settings?shop=${shop}`);
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map((order) => order.id));
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedOrders.length === 0) {
      showToast('Please select at least one order', true);
      return;
    }

    if (billingStatus && billingStatus.plan !== 'pro' && billingStatus.plan !== 'trial') {
      showToast('Bulk labels require Pro plan', true);
      navigate(`/billing?shop=${shop}`);
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await axios.post(
        `${API}/labels/create-bulk`,
        selectedOrders,
        {
          params: { shop: shop }
        }
      );

      const results = response.data.results || [];
      const successCount = results.filter((r) => r.status === 'success').length;
      const failedCount = results.filter((r) => r.status === 'failed').length;

      if (successCount > 0) {
        const trackingNumbers = results
          .filter((r) => r.status === 'success')
          .map((r) => r.tracking_number)
          .join(',');

        showToast(`${successCount} labels created successfully!`);

        if (trackingNumbers) {
          const downloadUrl = `${API}/labels/download-bulk?shop=${shop}&tracking_numbers=${trackingNumbers}`;
          window.open(downloadUrl, '_blank');
        }
      }

      if (failedCount > 0) {
        const failedOrders = results.filter((r) => r.status === 'failed');
        console.error('Failed orders:', failedOrders);
        showToast(`${failedCount} orders failed. Check console for details.`, true);
      }

      setSelectedOrders([]);
      fetchOrders(shop);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create bulk labels', true);
    } finally {
      setBulkProcessing(false);
    }
  };

  const rows = orders.map((order) => {
    const totalPrice = order.totalPriceSet?.shopMoney?.amount || '0';
    const currency = order.totalPriceSet?.shopMoney?.currencyCode || 'AUD';
    const customerName = order.customer
      ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim()
      : 'N/A';

    return [
      <Checkbox
        checked={selectedOrders.includes(order.id)}
        onChange={() => handleSelectOrder(order.id)}
        data-testid={`select-order-${order.name}-checkbox`}
      />,
      order.name,
      customerName,
      order.shippingAddress?.city || 'N/A',
      `${currency} ${parseFloat(totalPrice).toFixed(2)}`,
      <Badge tone="attention">{order.displayFulfillmentStatus || 'Unfulfilled'}</Badge>,
      <Button
        size="slim"
        onClick={() => handleViewOrder(order.id)}
        data-testid={`view-order-${order.name}-btn`}
      >
        View
      </Button>
    ];
  });

  if (loading) {
    return (
      <Page title="Orders">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  // Handle development/preview mode when no Shopify context is available
  const handleDemoLogin = () => {
    if (demoShopInput && demoShopInput.includes('.myshopify.com')) {
      localStorage.setItem('shop', demoShopInput);
      setShop(demoShopInput);
      setNoShopContext(false);
      navigate(`/orders?shop=${demoShopInput}`);
    } else {
      showToast('Please enter a valid shop domain (e.g., your-store.myshopify.com)', true);
    }
  };

  if (!shop || noShopContext) {
    return (
      <Page title="ShipMate">
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="400">
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Text variant="headingXl" as="h1">Welcome to ShipMate</Text>
                <p style={{ marginTop: '16px', fontSize: '16px' }}>Global Automated Shipping for Shopify</p>
              </div>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Development / Preview Mode</Text>
              <Banner tone="info">
                <p>No Shopify store context detected. This happens when accessing ShipMate outside of the Shopify Admin.</p>
              </Banner>
              
              <Text variant="bodyMd" as="p">
                <strong>For production use:</strong> Install ShipMate from the Shopify App Store or via the install link below.
              </Text>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Load an installed store:</Text>
                <InlineStack gap="200" align="start">
                  <div style={{ flex: 1, maxWidth: '400px' }}>
                    <TextField
                      value={demoShopInput}
                      onChange={setDemoShopInput}
                      placeholder="your-store.myshopify.com"
                      autoComplete="off"
                      data-testid="demo-shop-input"
                    />
                  </div>
                  <Button onClick={handleDemoLogin} data-testid="demo-login-btn">
                    Load Shop
                  </Button>
                </InlineStack>
                <Text variant="bodySm" as="p" tone="subdued">
                  Enter a shop domain that has already completed OAuth installation with ShipMate.
                  If the store hasn't installed ShipMate yet, you'll be prompted to complete installation first.
                </Text>
              </BlockStack>

              <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '16px', marginTop: '8px' }}>
                <Text variant="headingSm" as="h3">Install on a new store:</Text>
                <div style={{ marginTop: '12px' }}>
                  <Button
                    url={`${API}/install?shop=YOUR-STORE.myshopify.com`}
                    external
                    data-testid="install-link-btn"
                  >
                    Start Installation
                  </Button>
                  <Text variant="bodySm" as="p" tone="subdued" style={{ marginTop: '8px' }}>
                    Replace YOUR-STORE with your actual Shopify store name in the URL.
                  </Text>
                </div>
              </div>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">How ShipMate Works</Text>
              <Text variant="bodyMd" as="p">1. Install from Shopify Admin or via OAuth link</Text>
              <Text variant="bodyMd" as="p">2. Configure your sender address and shipping preferences</Text>
              <Text variant="bodyMd" as="p">3. ShipMate automatically creates shipping labels when orders are paid</Text>
              <Text variant="bodyMd" as="p">4. Tracking numbers sync back to Shopify automatically</Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  // Handle case where shop is set but OAuth installation is missing or token is invalid
  const handleClearShop = () => {
    localStorage.removeItem('shop');
    setShop(null);
    setStoreNotFound(false);
    setNoShopContext(true);
    navigate('/');
  };

  if (storeNotFound) {
    return (
      <Page title="ShipMate">
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="400">
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text variant="headingLg" as="h1">Installation Required</Text>
                <p style={{ marginTop: '8px', color: '#637381' }}>{shop}</p>
              </div>
            </BlockStack>
          </Card>

          <Banner tone="warning">
            <p><strong>{fetchError || 'Store not found'}</strong></p>
            <p style={{ marginTop: '8px' }}>
              This store has not completed the ShipMate OAuth installation, or the access token has expired.
            </p>
          </Banner>

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">To connect this store:</Text>
              <BlockStack gap="200">
                <Text variant="bodyMd" as="p">
                  <strong>1.</strong> Click the button below to start the OAuth installation flow
                </Text>
                <Text variant="bodyMd" as="p">
                  <strong>2.</strong> Authorize ShipMate in your Shopify Admin
                </Text>
                <Text variant="bodyMd" as="p">
                  <strong>3.</strong> You'll be redirected back here with full access to your orders
                </Text>
              </BlockStack>
              
              <InlineStack gap="200">
                <Button
                  primary
                  url={`${API}/install?shop=${shop}`}
                  external
                >
                  Install ShipMate on {shop}
                </Button>
                <Button onClick={handleClearShop}>
                  Try Different Store
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  return (
    <Page
      title="Unfulfilled Orders"
      primaryAction={{
        content: 'Settings',
        onAction: handleGoToSettings
      }}
      secondaryActions={[
        {
          content: 'Shipments',
          onAction: () => navigate(`/shipments?shop=${shop}`)
        },
        {
          content: 'Automation',
          onAction: () => navigate(`/automation?shop=${shop}`)
        },
        {
          content: 'Billing',
          onAction: () => navigate(`/billing?shop=${shop}`)
        }
      ]}
    >
      <BlockStack gap="400">
        {billingStatus && billingStatus.plan === 'trial_expired' && (
          <Banner tone="warning">
            <p>Your trial has expired. <a href={`/billing?shop=${shop}`}>Upgrade to continue using automation</a></p>
          </Banner>
        )}

        {selectedOrders.length > 0 && (
          <Card>
            <InlineStack align="space-between">
              <Text variant="bodyMd" as="p">{selectedOrders.length} order(s) selected</Text>
              <InlineStack gap="200">
                <Button onClick={() => setSelectedOrders([])}>Clear Selection</Button>
                <Button
                  primary
                  onClick={handleBulkGenerate}
                  loading={bulkProcessing}
                  data-testid="bulk-generate-labels-btn"
                >
                  Generate Labels
                </Button>
              </InlineStack>
            </InlineStack>
          </Card>
        )}

        <Card>
          {fetchError && !storeNotFound && (
            <Banner tone="warning" style={{ marginBottom: '16px' }}>
              <p><strong>Warning:</strong> {fetchError}</p>
            </Banner>
          )}
          {orders.length === 0 ? (
            <EmptyState
              heading="No unfulfilled orders"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No orders are waiting for fulfillment. This could mean:</p>
              <ul style={{ textAlign: 'left', marginTop: '8px' }}>
                <li>All orders have been fulfilled</li>
                <li>No orders have been placed yet</li>
                <li>Orders may be in Draft status (drafts are not included)</li>
              </ul>
            </EmptyState>
          ) : (
            <BlockStack gap="200">
              <InlineStack>
                <Checkbox
                  checked={selectedOrders.length === orders.length}
                  onChange={handleSelectAll}
                  label="Select all"
                  data-testid="select-all-checkbox"
                />
              </InlineStack>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['', 'Order', 'Customer', 'City', 'Total', 'Status', 'Action']}
                rows={rows}
              />
            </BlockStack>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}

export default OrdersPage;